import { format } from 'date-fns';
import { useEffectiveDateFormat } from 'hooks/useEffectiveDateFormat';
import { ReactNode, useSyncExternalStore } from 'react';
import { FormattedMessage } from 'react-intl';
import { NamedEntityMinDto } from 'types/other';
import { formatTimeOfDay } from 'utils/date';
import { t } from 'utils/getTranslations.ts';
import { displayName, useLastNameFirst } from 'utils/nameOrder';
import { isNewDesignCopy, onNewDesignCopyChange } from 'utils/newDesignCopy';

interface AuditInfoProps {
    createdAt?: string | Date;
    createdByEmployee?: NamedEntityMinDto;
    lastModifiedAt?: string | Date;
    changedByEmployee?: NamedEntityMinDto;
}

function formatDateTime(date: string | Date | undefined) {
    if (!date) return '';
    try {
        const dateObj = typeof date === 'string' ? new Date(date) : date;
        return format(dateObj, "MMM dd, yyyy 'at' HH:mm");
    } catch {
        return '';
    }
}

/**
 * A line of the new design: one sentence, its date in the user's format, as the record's other dates are. A
 * component of its own so only the new design asks for that format.
 */
function AuditLine({ id, name, date }: { id: string; name: string; date: string | Date | undefined }) {
    const dateFormat = useEffectiveDateFormat();
    let when: { dated: string; date?: string; time?: string } = { dated: 'no' };
    try {
        if (date) {
            const at = new Date(date);
            when = { dated: 'yes', date: format(at, dateFormat), time: formatTimeOfDay(at) };
        }
    } catch {
        // An unreadable date leaves the sentence without one.
    }
    // The message hands React its parts as a list, so the element among them carries a key.
    return (
        <p className="text-fg-secondary new:text-caption text-xs">
            <FormattedMessage
                id={id}
                values={{ ...when, name, b: (chunks: ReactNode) => <strong key="b">{chunks}</strong> }}
            />
        </p>
    );
}

export default function AuditInfo({ createdAt, createdByEmployee, lastModifiedAt, changedByEmployee }: AuditInfoProps) {
    const lastNameFirst = useLastNameFirst();
    // Not `useIsNewDesign`: a record view also renders this line where no store is around it. Heard, not read once:
    // a modal holding the line stays mounted when the design is switched.
    const isNewDesign = useSyncExternalStore(onNewDesignCopyChange, isNewDesignCopy);

    const getEmployeeName = (employee?: NamedEntityMinDto) => {
        return (employee && displayName(employee, lastNameFirst)) || '-';
    };

    const updatedBy = changedByEmployee ?? createdByEmployee;

    if (isNewDesign) {
        return (
            <div className="flex flex-col gap-1">
                <AuditLine id="audit.created" name={getEmployeeName(createdByEmployee)} date={createdAt} />
                <AuditLine id="audit.updated" name={getEmployeeName(updatedBy)} date={lastModifiedAt ?? createdAt} />
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-1">
            <p className="text-fg-secondary new:text-caption text-xs">
                <strong>{t('created-by')}</strong> {getEmployeeName(createdByEmployee)} {t('coi.on')}{' '}
                {formatDateTime(createdAt)}
            </p>
            <p className="text-fg-secondary new:text-caption text-xs">
                <strong>{t('updated-by')}</strong> {getEmployeeName(updatedBy)} {t('coi.on')}{' '}
                {formatDateTime(lastModifiedAt ?? createdAt)}
            </p>
        </div>
    );
}
